import { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Button,
  CircularProgress,
  IconButton,
  Divider,
} from '@mui/material';
import { Star, ArrowBackIosNew, Edit, Delete, ShoppingCart } from '@mui/icons-material';
import { productService } from '../../services/productService';
import toast from 'react-hot-toast';

const placeholderProducts = [
  {
    id: 1,
    name: 'Elegant Macau Chair',
    category: 'Furniture',
    price: 225.0,
    currency: '₹',
    stock: 14,
    rating: 4.8,
    description:
      'Smoke-lacquered wood frame, soft velvet upholstered seat, astonishing comfort for modern sales spaces.',
    image:
      'https://images.unsplash.com/photo-1524758631624-e2822e304c36?auto=format&fit=crop&w=1200&q=80',
    sku: 'MAC-CHAIR-01',
    variants: ['Standard', 'Premium', 'Deluxe'],
    supplier: 'Lyra Furnishings',
    dimensions: '24 x 18 x 33 in',
    weight: '12 kg',
    isActive: true,
  },
  {
    id: 2,
    name: 'Stellar Espresso Machine',
    category: 'Appliances',
    price: 18999.0,
    currency: '₹',
    stock: 8,
    rating: 4.6,
    description:
      'Commercial-grade 15-bar pump espresso maker with integrated milk frother and smart fluid management.',
    image:
      'https://images.unsplash.com/photo-1516379093141-0d2cf2b18f0d?auto=format&fit=crop&w=1200&q=80',
    sku: 'ESP-15B-PRO',
    variants: ['Silver', 'Black'],
    supplier: 'Caffina India',
    dimensions: '20 x 28 x 34 in',
    weight: '17 kg',
    isActive: true,
  },
  {
    id: 3,
    name: 'Premium High-Definition Display',
    category: 'Electronics',
    price: 35999.0,
    currency: '₹',
    stock: 6,
    rating: 4.9,
    description:
      '27-inch 4K UHD professional display with IPS panel, high color fidelity, and anti-glare coating for product presentation.',
    image:
      'https://images.unsplash.com/photo-1545239351-1141bd82e8a6?auto=format&fit=crop&w=1200&q=80',
    sku: 'MON-27-4KUHD',
    variants: ['Standard', 'Pro'],
    supplier: 'ScreenTech',
    dimensions: '25 x 16 x 2 in',
    weight: '7.5 kg',
    isActive: true,
  },
];

export const Product = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await productService.getProducts();
      const data = response.data?.data || response.data || [];
      setProducts(Array.isArray(data) && data.length ? data : placeholderProducts);
    } catch (err) {
      toast.error('Unable to load products, loading sample inventory.');
      setProducts(placeholderProducts);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const closeDetail = () => setSelectedProduct(null);

  const ProductDetailCard = ({ product }) => {
    if (!product) {
      return (
        <Box sx={{ bgcolor: 'background.paper', p: { xs: 2, sm: 2.5, md: 3 }, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Product Details
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Click any product card to view detailed information and specifications.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ bgcolor: 'background.paper', p: { xs: 2, sm: 2.5, md: 3 }, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5, gap: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {product.name}
          </Typography>
          <IconButton color="primary" onClick={closeDetail} size="small">
            <ArrowBackIosNew fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ borderRadius: 2, overflow: 'hidden', mb: 2.5 }}>
          <CardMedia
            component="img"
            height="220"
            image={product.image || 'https://via.placeholder.com/640x360?text=No+image'}
            alt={product.name}
          />
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Category: {product.category || 'N/A'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          SKU: {product.sku || 'N/A'} • Supplier: {product.supplier || 'N/A'}
        </Typography>

        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          {product.currency || '₹'}{(product.selling_price_with_gst || product.price)?.toFixed(2) || '0.00'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2.5 }}>
          <Star fontSize="small" sx={{ color: '#f59e0b' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{product.rating || '0.0'}</Typography>
          <Typography variant="body2" color="text.secondary">({product.stock || 0} unit{product.stock === 1 ? '' : 's'} in stock)</Typography>
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Description
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          {product.description || 'No description available.'}
        </Typography>

        <Box sx={{ mb: 2.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Specifications</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Dimensions: {product.dimensions || 'N/A'}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Weight: {product.weight || 'N/A'}</Typography>
          <Typography variant="caption" color="text.secondary">Variants: {product.variants?.join(' • ') || 'N/A'}</Typography>
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.5 }}>
          <Chip label={product.isActive ? 'Active' : 'Inactive'} color={product.isActive ? 'success' : 'default'} size="small" />
          <Chip label={`Stock: ${product.stock || 0}`} variant="outlined" size="small" />
          <Chip label={`${product.category || 'N/A'}`} size="small" />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button startIcon={<Edit />} variant="contained" color="primary" size="small">
            Edit
          </Button>
          <Button startIcon={<Delete />} variant="outlined" color="error" size="small">
            Delete
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <div className="p-6">
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: '100%', width: '100%' }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Product Catalog
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Browse, manage, and organize your product inventory
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
          {/* Left Column: Product List */}
          <Grid item xs={12} md={8}>
            <Box sx={{ mb: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Products
              </Typography>
              <Button size="small" variant="contained" startIcon={<ShoppingCart />} onClick={fetchProducts}>
                Refresh
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
                <CircularProgress />
              </Box>
            ) : products.length === 0 ? (
              <Card sx={{ p: 4, textAlign: 'center', bgcolor: 'background.paper' }}>
                <Typography color="textSecondary">No products available. Add items to populate the catalog.</Typography>
              </Card>
            ) : (
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                {products.map((product) => (
                  <Grid item xs={12} sm={6} md={4} key={product.id}>
                    <Card
                      sx={{
                        borderRadius: 2,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'pointer',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 24px rgba(0,0,0,0.10)'
                        },
                      }}
                      onClick={() => setSelectedProduct(product)}
                    >
                      <CardMedia
                        component="img"
                        height="160"
                        image={product.image || 'https://via.placeholder.com/480x320?text=No+image'}
                        alt={product.name}
                        sx={{ backgroundColor: '#f5f5f5' }}
                      />
                      <CardContent sx={{ pb: 1.5, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.3 }} noWrap>
                          {product.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {product.description || 'No description'}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 'auto' }}>
                          <Chip size="small" label={product.category || 'Unknown'} />
                          <Chip size="small" label={`₹${(product.selling_price_with_gst || product.price)?.toFixed(2) || '0.00'}`} color="success" variant="outlined" />
                        </Box>
                      </CardContent>
                      <CardActions sx={{ justifyContent: 'space-between', px: 1.5, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
                        <Chip size="small" label={product.stock > 5 ? 'In stock' : 'Low stock'} color={product.stock > 5 ? 'success' : 'warning'} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Star fontSize="small" sx={{ color: '#f59e0b' }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {product.rating || '0.0'}
                          </Typography>
                        </Box>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>

          {/* Right Column: Product Details */}
          <Grid item xs={12} md={4}>
            <ProductDetailCard product={selectedProduct} />
          </Grid>
        </Grid>
      </Box>
    </div>
  );
};
